import { useState } from 'react';
import { Download, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';

/**
 * Delivery video player with a guaranteed real-file Download.
 *
 * Props:
 *  - videoUrl: string — public/signed URL for inline playback.
 *  - thumbnail / fileName / fileSize — UI metadata.
 *  - projectId / deliveryId / fileIndex — preferred path: hit the
 *    delivery-scoped backend route which enforces ACL and serves the
 *    original bytes with `Content-Disposition: attachment`.
 *  - storagePath / bucket — legacy fallback path when we don't yet know
 *    the deliveryId (older delivery messages persisted before this fix).
 *  - onDownload — escape hatch.
 *
 * Behavior:
 *  - Desktop: blob → object URL → <a download>.click() → revoke.
 *  - Mobile (iOS/Android): we still fetch the blob, but `<a download>`
 *    on iOS is unreliable, so we fall back to opening the response
 *    URL via a `Blob` that the OS hands to the share sheet. If that
 *    fails too we navigate to the proxy URL with `?token=...` (still
 *    sends Content-Disposition so iOS Safari downloads / shares it).
 *  - Errors always reset the spinner and surface
 *    "Download failed. Try again." inline beneath the video.
 */
export function VideoPlayer({
  videoUrl,
  thumbnail,
  fileName,
  fileSize,
  projectId,
  deliveryId,
  fileIndex = 0,
  storagePath,
  bucket,
  onDownload,
}) {
  const { session } = useAuth();
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const effectiveUrl = videoUrl || null;
  const token = session?.access_token || null;

  // Build the proxy URL we want to hit, preferring the delivery-scoped
  // route (real ACL) and falling back to the legacy generic route.
  const proxyUrl = (() => {
    if (projectId && deliveryId) {
      return `${API}/api/files/download-delivery/${projectId}/${deliveryId}/${fileIndex}`;
    }
    if (storagePath) {
      const qs = new URLSearchParams();
      qs.set('bucket', bucket || 'project-files');
      if (fileName) qs.set('filename', fileName);
      return `${API}/api/files/download/${encodeURI(storagePath)}?${qs.toString()}`;
    }
    return null;
  })();

  const isMobile = typeof navigator !== 'undefined'
    && /android|iphone|ipad|ipod/i.test(navigator.userAgent || '');

  const triggerSave = (blob, suggestedName) => {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = suggestedName || 'video.mp4';
    a.rel = 'noopener noreferrer';
    // Some browsers ignore programmatic clicks unless the anchor is in DOM.
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
  };

  const download = async () => {
    if (onDownload) return onDownload();
    if (downloading) return;
    setDownloadError(null);

    // No proxy + no signed URL → nothing to do.
    if (!proxyUrl && !effectiveUrl) {
      setDownloadError('Download failed. Try again.');
      return;
    }

    setDownloading(true);
    try {
      if (proxyUrl && token) {
        const res = await fetch(proxyUrl, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });
        if (!res.ok) throw new Error(`Proxy ${res.status}`);
        const blob = await res.blob();
        const cd = res.headers.get('Content-Disposition') || '';
        const m = /filename="([^"]+)"/i.exec(cd);
        const name = (m && m[1]) || fileName || 'video.mp4';

        if (isMobile) {
          // iOS Safari + most Android browsers respect the download
          // attribute on object URLs derived from blobs. If the OS
          // refuses, surfacing the share sheet via window.open is the
          // documented mobile-friendly fallback.
          try {
            triggerSave(blob, name);
          } catch (_e) {
            const fallbackUrl = URL.createObjectURL(blob);
            window.open(fallbackUrl, '_blank', 'noopener');
            setTimeout(() => URL.revokeObjectURL(fallbackUrl), 8000);
          }
        } else {
          triggerSave(blob, name);
        }
        return;
      }

      // Last-resort: direct signed URL with download attribute. Cross-origin
      // browsers may navigate, but the file ends up on disk.
      if (effectiveUrl) {
        const a = document.createElement('a');
        a.href = effectiveUrl;
        a.download = fileName || '';
        a.rel = 'noopener noreferrer';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      throw new Error('No download source');
    } catch (_e) {
      setDownloadError('Download failed. Try again.');
    } finally {
      // Crucial: reset state EVEN if the user cancelled the save dialog
      // or the network errored mid-flight. No infinite "Downloading…".
      setDownloading(false);
    }
  };

  const showDownloadable = effectiveUrl || proxyUrl;

  return (
    <div data-testid="video-player" className="space-y-2">
      <div className="aspect-video bg-black rounded-xl border border-gray-200 overflow-hidden relative">
        {effectiveUrl && !failed ? (
          <video
            data-testid="delivery-video"
            src={effectiveUrl}
            poster={thumbnail || undefined}
            controls
            playsInline
            preload="metadata"
            className="w-full h-full object-contain bg-black"
            onError={() => setFailed(true)}
          />
        ) : (
          <div
            data-testid="video-player-fallback"
            className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gray-900 text-gray-300 px-6 text-center"
          >
            <AlertCircle className="w-6 h-6 text-gray-400" />
            <p className="text-sm">Video preview unavailable — download file.</p>
            {showDownloadable && (
              <button
                type="button"
                data-testid="video-fallback-download-btn"
                onClick={download}
                disabled={downloading}
                className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-60 text-xs text-white transition-colors"
              >
                {downloading
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Download className="w-3.5 h-3.5" />}
                {downloading ? 'Downloading…' : 'Download'}
              </button>
            )}
          </div>
        )}
      </div>

      {(fileName || fileSize || showDownloadable) && (
        <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
          <div className="min-w-0 flex-1 truncate">
            {fileName && <span className="text-gray-700">{fileName}</span>}
            {fileName && fileSize && <span className="text-gray-300"> · </span>}
            {fileSize && <span>{fileSize}</span>}
          </div>
          {showDownloadable && (
            <button
              type="button"
              data-testid="video-download-btn"
              onClick={download}
              disabled={downloading}
              className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-800 disabled:opacity-60 transition-colors shrink-0"
            >
              {downloading
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Download className="w-3.5 h-3.5" />}
              <span>{downloading ? 'Downloading…' : 'Download'}</span>
            </button>
          )}
        </div>
      )}

      {downloadError && (
        <p
          data-testid="video-download-error"
          className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2"
        >
          {downloadError}
        </p>
      )}
    </div>
  );
}
